import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

type AppRole = Database['public']['Enums']['app_role'];

interface Organization {
  id: string;
  name: string;
  subdomain: string | null;
  logo_url: string | null;
  plan: string | null;
  is_active: boolean | null;
  business_category: string | null;
}

// Role priority for determining primary role (highest privilege first)
const ROLE_PRIORITY: AppRole[] = [
  'superadmin', 'admin', 'manager', 'hr', 'finance', 
  'procurement', 'warehouse', 'sales', 'production', 'logistics',
  'developer', 'designer', 'marketer', 'caller'
];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null; // Primary role (highest privilege)
  roles: AppRole[]; // All assigned roles
  organization: Organization | null;
  loading: boolean;
  hasRole: (roleToCheck: AppRole) => boolean;
  hasAnyRole: (rolesToCheck: AppRole[]) => boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  // Calculate primary role (highest privilege)
  const role = roles.length > 0 
    ? ROLE_PRIORITY.find(r => roles.includes(r)) || null 
    : null;

  // Helper functions for role checking - memoized to prevent re-renders
  const hasRole = useCallback((roleToCheck: AppRole) => roles.includes(roleToCheck), [roles]);
  const hasAnyRole = useCallback((rolesToCheck: AppRole[]) => rolesToCheck.some(r => roles.includes(r)), [roles]);

  useEffect(() => {
    let isMounted = true;

    // Set up auth state listener FIRST (handles ONGOING changes, not initial load)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer to avoid deadlock with Supabase internals
          setTimeout(() => {
            if (isMounted) fetchUserData(session.user.id);
          }, 0);
        } else {
          setRoles([]);
          setOrganization(null);
        }
      }
    );

    // INITIAL load - await everything before setting loading=false
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          await fetchUserData(session.user.id);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserData = useCallback(async (userId: string) => {
    // Parallel fetch: profiles + user_roles
    const [profileResult, roleResult] = await Promise.all([
      supabase
        .from('profiles')
        .select('organization_id, status')
        .eq('id', userId)
        .maybeSingle(),
      supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId),
    ]);

    const profileData = profileResult.data;
    const roleData = roleResult.data;

    // Check if user is suspended or terminated
    if (profileData?.status === 'suspended') {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setRoles([]);
      setOrganization(null);
      setTimeout(() => {
        toast.error('Your account has been suspended. Please contact your administrator.');
      }, 100);
      return;
    }

    if (profileData?.status === 'terminated') {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setRoles([]);
      setOrganization(null);
      setTimeout(() => {
        toast.error('Your account has been terminated.');
      }, 100);
      return;
    }

    if (roleData && roleData.length > 0) {
      setRoles(roleData.map(r => r.role));
    } else {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setRoles([]);
      setOrganization(null);
      setTimeout(() => {
        toast.error("You don't have authority to access this platform. Contact your administrator.");
      }, 100);
      return;
    }

    if (profileData?.organization_id) {
      const { data: orgData } = await supabase
        .from('organizations')
        .select('id, name, subdomain, logo_url, plan, is_active, business_category')
        .eq('id', profileData.organization_id)
        .maybeSingle();

      if (orgData) {
        setOrganization(orgData);
      }
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth`
      }
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRoles([]);
    setOrganization(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      role, 
      roles, 
      organization, 
      loading, 
      hasRole,
      hasAnyRole,
      signIn, 
      signInWithGoogle, 
      signOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
